import { createRouter } from "@tanstack/solid-router";
import { rootRoute } from "@/routes/__root";
import { NotFoundRoute } from "@/components/not-found-route";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminAuditRoute } from "@/routes/_app/admin.audit";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import { adminBibleVersionsRoute } from "@/routes/_app/admin.bible-versions";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTrashRoute } from "@/routes/_app/profile.trash";
import { bibleReaderRoute } from "@/routes/_app/bible.$version.$bookAbbr.$chapter";
import { searchRoute } from "@/routes/_app/search";
import { dictionaryRoute } from "@/routes/_app/dictionary";
import { highlightsRoute } from "@/routes/_app/highlights";
import { notesRoute } from "@/routes/_app/notes";
import { bookmarksRoute } from "@/routes/_app/bookmarks";
import { plansRoute } from "@/routes/_app/plans";
import { planDetailRoute } from "@/routes/_app/plans.$planId";
import { analyticsRoute } from "@/routes/_app/analytics";
import { achievementsRoute } from "@/routes/_app/achievements";
import { settingsRoute } from "@/routes/_app/settings";
import { notificationsRoute } from "@/routes/_app/notifications";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — redirects to last-read Bible passage
    bibleReaderRoute, // path: "/bible/$version/$bookAbbr/$chapter"
    searchRoute, // path: "/search"
    dictionaryRoute, // path: "/dictionary"
    highlightsRoute, // path: "/highlights"
    notesRoute, // path: "/notes"
    bookmarksRoute, // path: "/bookmarks" — favorite verses
    plansRoute, // path: "/plans" — offline reading plans catalog + progress
    planDetailRoute, // path: "/plans/$planId" — plan days, mark-as-read, reader links
    analyticsRoute, // path: "/analytics" — study progress + bar charts
    achievementsRoute, // path: "/achievements" — reading streak + XP/badges (gamification-core)
    settingsRoute, // path: "/settings" — offline Bible download manager
    notificationsRoute, // path: "/notifications" — invitations + system notification prefs
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminFlagsRoute,
      adminMetricsRoute,
      adminIntegrationsRoute,
      adminStorageRoute,
      adminAuditRoute,
      adminThemeAuditRoute,
      adminThemesRoute,
      adminFontsRoute,
      adminBibleVersionsRoute,
      adminEmailRoute,
      adminEmailCampaignsRoute,
    ]),
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTrashRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // Cualquier URL desconocida muestra un estado "no encontrado" con acción a un
  // lugar seguro, en lugar de una pantalla en blanco.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
